import {Component, computed, effect, inject, input, output} from '@angular/core';
import {
  CreateInvitationDocumentRequestParams,
  DeleteInvitationDocumentRequestParams,
  DownloadInvitationDocumentRequestParams,
  EditInvitationDocumentRequestParams,
  InvitationUploadDocumentEditRequest,
  ProviderTenderRfqControllerService,
} from '@frontend/shared-api';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {firstValueFrom} from 'rxjs';
import {rxResource} from '@angular/core/rxjs-interop';
import {ToastService} from '@frontend/toast';
import {ProviderDocumentUploadComponent} from '../provider-document-upload/provider-document-upload.component';
import {FileDownloadService} from '../../../../../../shared/utils/file-download.service';
import {DocumentPreviewDialogService} from '../../../../../../shared/components/dialog/document-preview-dialog/document-preview-dialog.service';
import {DirectusService} from '@frontend/directus';
import {UploadDocumentProgressHeaderComponent} from '../upload-document-progress-header/upload-document-progress-header.component';

@Component({
  selector: 'app-rfq-document-upload-content',
  imports: [
    LoadingComponent,
    ProviderDocumentUploadComponent,
    UploadDocumentProgressHeaderComponent,
  ],
  templateUrl: './rfq-document-upload-content.component.html',
})
export class RfqDocumentUploadContentComponent {
  private readonly directusService = inject(DirectusService);
  private readonly providerTenderRfqController = inject(ProviderTenderRfqControllerService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly documentPreviewDialogService = inject(DocumentPreviewDialogService);
  private readonly toastService = inject(ToastService);

  readonly tenderId = input.required<number>();
  readonly isEditable = input<boolean>(true);
  readonly phaseCompleted = output<boolean>();

  protected readonly uploadDocumentsCategoryResource = rxResource({
    params: () => true,
    stream: () => this.directusService.loadInvitationUploadDocuments(),
  });

  protected readonly invitationDocumentsResource = rxResource({
    params: () => ({tenderId: this.tenderId()}),
    stream: ({params: request}) =>
      this.providerTenderRfqController.getTenderInvitationDocuments({tenderId: request.tenderId}),
  });

  protected readonly totalCountMandatory = computed(
    () => this.uploadDocumentsCategoryResource.value()?.filter((doc) => doc.mandatory).length ?? 0,
  );

  protected readonly completedCountMandatory = computed(() => {
    const definitions = this.uploadDocumentsCategoryResource.value();
    const uploadedDocs = this.invitationDocumentsResource.value();

    if (!definitions || !uploadedDocs) return 0;

    return definitions
      .filter((doc) => doc.mandatory)
      .filter((def) => uploadedDocs.some((d) => d.invitationUploadDocumentUuid === def.id)).length;
  });

  protected readonly isPhaseCompleted = computed(
    () => this.totalCountMandatory() === this.completedCountMandatory(),
  );

  constructor() {
    effect(() => this.phaseCompleted.emit(this.isPhaseCompleted()));
  }

  protected async onUploadDocument(theFile: CreateInvitationDocumentRequestParams) {
    try {
      await firstValueFrom(
        this.providerTenderRfqController.createInvitationDocument({
          tenderId: this.tenderId(),
          invitationUploadDocumentUuid: theFile.invitationUploadDocumentUuid,
          version: theFile.version,
          statusUuid: theFile.statusUuid,
          fileName: theFile.fileName,
          file: theFile.file,
        } as CreateInvitationDocumentRequestParams),
      );
      void this.toastService.showSuccessMessage();
      this.invitationDocumentsResource.reload();
    } catch (err) {
      console.error('Upload failed:', err);
    }
  }

  protected async onPreviewDocument(documentId: number): Promise<void> {
    try {
      const blob = await firstValueFrom(
        this.providerTenderRfqController.downloadInvitationDocument({
          tenderId: this.tenderId(),
          documentId,
        }),
      );
      this.documentPreviewDialogService.open(blob);
    } catch (err) {
      console.error('Preview failed:', err);
    }
  }

  protected async onDownloadDocument(documentId: number): Promise<void> {
    try {
      const params: DownloadInvitationDocumentRequestParams = {
        tenderId: this.tenderId(),
        documentId,
      };
      const blob = await firstValueFrom(
        this.providerTenderRfqController.downloadInvitationDocument(params, 'response'),
      );
      this.fileDownloadService.downloadFromResponse(blob);
      void this.toastService.showSuccessMessage();
    } catch (err) {
      console.error('Download failed:', err);
    }
  }

  protected async onEditDocument(event: InvitationUploadDocumentEditRequest): Promise<void> {
    const params: EditInvitationDocumentRequestParams = {
      tenderId: this.tenderId(),
      invitationUploadDocumentEditRequest: event,
    };

    try {
      await firstValueFrom(this.providerTenderRfqController.editInvitationDocument(params));
      void this.toastService.showSuccessMessage();
      this.invitationDocumentsResource.reload();
    } catch (err) {
      console.error('Editing failed:', err);
    }
  }

  protected async onDeleteDocument(documentId: number): Promise<void> {
    try {
      const params: DeleteInvitationDocumentRequestParams = {
        tenderId: this.tenderId(),
        documentId,
      };
      await firstValueFrom(this.providerTenderRfqController.deleteInvitationDocument(params));
      void this.toastService.showSuccessMessage();
      this.invitationDocumentsResource.reload();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }
}
